import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { graphQLClient } from "../graphqlClient";
import { GET_MY_FEATURES } from "../api/queries";
import { useReactQueryErrorReporter } from "./use-error-reporter";
import { useFullCurrentUser } from "./useCurrentUser";
import { useStores } from "./useStores";

const getPreferredUserFeatures = (allFeatures, preferredUserPath) => {
  const defaultFeatures = allFeatures?.[0]?.features || [];

  if (!preferredUserPath) {
    return defaultFeatures;
  }

  return (
    allFeatures?.find((f) => f.path === preferredUserPath)?.features ||
    defaultFeatures
  );
};

// This will only retrieve the product features for the climber at most once every 10 minutes.
// If more, it will return cached values and do an update in the background.
// Example of how to use:
//   const { matchesProductPlan, matchesProductPlans } = useProductFeatures();
//   if (matchesProductPlan("ms-staff")) {
//     console.log("User is in MS");
//   }
//   if (matchesProductPlans(["ms-staff", "wbl-staff"])) {
//     console.log("User is in MS or WBL");
//   }
export const useProductFeatures = () => {
  const { activeUserRecord } = useFullCurrentUser();
  const { userStore } = useStores();
  const preferredUserPath = userStore?.getPreferredUserPath();

  const { data, error, isLoading } = useQuery({
    queryKey: ["getMyFeatures"],
    queryFn: () => graphQLClient.request(GET_MY_FEATURES),
    staleTime: 1000 * 60 * 10, // Consider data fresh for 10 minutes (same as production appsync cache duration)
    cacheTime: 1000 * 60 * 60 * 48, // Keep unused data in cache for 48 hours
  });

  useReactQueryErrorReporter("Could not find product subscription", error);

  const getDebugPlans = () => {
    try {
      const storedPlan = localStorage.getItem("debug_product_plan");
      const debugProductPlans = storedPlan ? JSON.parse(storedPlan) : null;
      return debugProductPlans;
    } catch (e) {
      console.error("Invalid debug_product_plan in localStorage", e);
      return null;
    }
  };

  const debugProductPlans = getDebugPlans();

  const featuresByPath = data?.getMyFeatures || [];

  const features =
    activeUserRecord?.role === "climber"
      ? getPreferredUserFeatures(data?.getMyFeatures, preferredUserPath)
      : data?.getMyFeatures?.flatMap((f) => f.features); // Educators get their product features combined.

  const productPlans = debugProductPlans || features || [];

  const proskillPlans = productPlans.filter((plan) =>
    plan.startsWith("proskills"),
  );

  const wblPlans = productPlans?.filter((plan) => plan.startsWith("wbl")) || [];
  const msPlans = productPlans?.filter((plan) => plan.startsWith("ms-")) || [];

  // For internal developer use only.
  const setDebugProductPlan = useCallback((newPlan) => {
    if (Array.isArray(newPlan)) {
      localStorage.setItem("debug_product_plan", JSON.stringify(newPlan));
      //setDebugProductPlansState(newPlan);
    } else {
      localStorage.removeItem("debug_product_plan");
      //setDebugProductPlansState(null);
    }
    //setDebugCounter(c => c + 1);
  }, []);

  // Checks if a single plan matches the users's plans
  const matchesProductPlan = useCallback(
    (plan) => {
      return productPlans.includes(plan);
    },
    [productPlans],
  );

  // Checks if any of the plans in the array match the users's plans
  const matchesProductPlans = useCallback(
    (plans) => {
      if (!plans) {
        return false;
      }
      return productPlans.some((plan) => plans.includes(plan));
    },
    [productPlans],
  );

  return {
    productPlans,
    featuresByPath,
    setDebugProductPlan,
    isDebugOverride: debugProductPlans !== null,
    matchesProductPlan,
    matchesProductPlans,
    proskillPlans,
    msPlans, // TODO - remove this
    wblPlans, // TODO - remove this
    isLoading,
  };
};
